import { ref, set, get, push, onValue, update } from 'firebase/database';
import { db } from '../lib/firebase';
import { API_ENDPOINTS } from '../config/api';

export type NotificationType = 'activity_completed' | 'streak_missed' | 'provocation' | 'challenge_received' | 'challenge_finished' | 'friend_request' | 'friend_request_accepted' | 'inactivity_alert' | 'session_invite' | 'stop_game_invite';

export interface AppNotification {
  id?: string;
  type: NotificationType;
  message: string;
  fromUserId?: string;
  fromUserName?: string;
  fromUserPhoto?: string;
  timestamp: number;
  read: boolean;
  metadata?: any;
}

export const sendNotification = async (targetUserId: string, notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'>) => {
  const notifRef = ref(db, `notifications/${targetUserId}`);
  const newNotifRef = push(notifRef);
  
  // Firebase Realtime DB throws an error if any value is undefined.
  // We sanitize the notification object to remove undefined properties.
  const safeNotification = Object.fromEntries(
    Object.entries(notification).filter(([_, v]) => v !== undefined)
  );

  await set(newNotifRef, {
    ...safeNotification,
    timestamp: Date.now(),
    read: false
  });

  let actionUrl = 'https://lingualearn.web.app';
  if (notification.type === 'provocation') {
    actionUrl = `https://lingualearn.web.app/provocation/${newNotifRef.key}`;
  } else if (notification.type === 'friend_request') {
    actionUrl = 'https://lingualearn.web.app/social?tab=requests';
  } else if (notification.type === 'friend_request_accepted') {
    actionUrl = `https://lingualearn.web.app/profile/${notification.fromUserId}`;
  } else if (notification.type === 'inactivity_alert') {
    actionUrl = 'https://lingualearn.web.app/social?tab=challenges';
  } else if (notification.type === 'challenge_received' || notification.type === 'challenge_finished') {
    if (notification.metadata?.challengeId) {
      actionUrl = `https://lingualearn.web.app/challenges/${notification.metadata.challengeId}`;
    } else {
      actionUrl = 'https://lingualearn.web.app/challenges';
    }
  } else if (notification.type === 'session_invite') {
    if (notification.metadata?.sessionId) {
      actionUrl = `https://lingualearn.web.app/session/${notification.metadata.sessionId}`;
    } else {
      actionUrl = 'https://lingualearn.web.app/social';
    }
  } else if (notification.type === 'stop_game_invite') {
    if (notification.metadata?.roomCode) {
      actionUrl = `https://lingualearn.web.app/stop-game?room=${notification.metadata.roomCode}`;
    } else {
      actionUrl = 'https://lingualearn.web.app/stop-game';
    }
  }

  // Call the Node.js backend to send actual Push and Email
  try {
    fetch(API_ENDPOINTS.notify, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        targetUserId,
        title: getNotificationTitle(notification.type, notification.fromUserName),
        body: notification.message,
        type: notification.type,
        actionUrl
      }),
      keepalive: true
    }).catch(err => console.error('Failed to trigger push notification:', err));
  } catch (e) {
    console.error('Error hitting notify endpoint', e);
  }
};

const getNotificationTitle = (type: NotificationType, userName?: string): string => {
  const name = userName || 'Someone';
  switch (type) {
    case 'provocation': return `${name} provoked you!`;
    case 'friend_request': return `New friend request from ${name}`;
    case 'friend_request_accepted': return `${name} accepted your request`;
    case 'inactivity_alert': return `Your friend ${name} needs you!`;
    case 'challenge_received': return `New Challenge from ${name}!`;
    case 'challenge_finished': return `Challenge Finished vs ${name}!`;
    case 'session_invite': return `${name} invited you to join a Study Session! 📖`;
    case 'stop_game_invite': return `${name} invited you to play STOP! 🛑`;
    default: return 'New LinguaLearn Notification';
  }
};

export const getNotifications = (userId: string, callback: (notifications: AppNotification[]) => void) => {
  const notifRef = ref(db, `notifications/${userId}`);
  return onValue(notifRef, (snapshot) => {
    const data = snapshot.val() || {};
    const notifications: AppNotification[] = Object.entries<any>(data).map(([id, val]) => ({
      id,
      ...val
    }));
    
    // Sort descending by timestamp
    notifications.sort((a, b) => b.timestamp - a.timestamp);
    callback(notifications);
  });
};

export const markNotificationAsRead = async (userId: string, notificationId: string) => {
  const notifRef = ref(db, `notifications/${userId}/${notificationId}`);
  await update(notifRef, { read: true });
};

export const markAllNotificationsAsRead = async (userId: string, notificationIds: string[]) => {
  const updates: Record<string, any> = {};
  notificationIds.forEach(id => {
    updates[`${id}/read`] = true;
  });
  
  if (Object.keys(updates).length > 0) {
    const notifRef = ref(db, `notifications/${userId}`);
    await update(notifRef, updates);
  }
};

export const notifyFollowers = async (userId: string, type: NotificationType, message: string, metadata?: any) => {
  // First, get the user's followers
  const followersRef = ref(db, `social/followers/${userId}`);
  const followersSnap = await get(followersRef);
  const followers = followersSnap.val() || {};
  
  const userRef = ref(db, `users/${userId}`);
  const userSnap = await get(userRef);
  const userData = userSnap.val();
  
  if (!userData) return;
  
  const followerIds = Object.keys(followers);
  
  // Send notification to each follower
  const notification: Omit<AppNotification, 'id' | 'timestamp' | 'read'> = {
    type,
    message,
    fromUserId: userId,
    fromUserName: userData.username,
    fromUserPhoto: userData.profilePhotoUrl,
    metadata
  };
  
  const promises = followerIds.map(id => sendNotification(id, notification));
  await Promise.all(promises);
};

export const sendProvocation = async (currentUserId: string, targetUserId: string, message?: string) => {
  const userRef = ref(db, `users/${currentUserId}`);
  const userSnap = await get(userRef);
  const userData = userSnap.val();
  
  if (!userData) return;
  
  await sendNotification(targetUserId, {
    type: 'provocation',
    message: message || `${userData.username || 'A friend'} noticed you missed a day and sent you a provocation! Get back on track! 🔥`,
    fromUserId: currentUserId,
    fromUserName: userData.username,
    fromUserPhoto: userData.profilePhotoUrl
  });
};

export const getNotificationById = async (userId: string, notificationId: string): Promise<AppNotification | null> => {
  const notifRef = ref(db, `notifications/${userId}/${notificationId}`);
  const snapshot = await get(notifRef);
  if (snapshot.exists()) {
    return { id: notificationId, ...snapshot.val() } as AppNotification;
  }
  return null;
};
